import React, { useState, useCallback, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "sonner";
import { logger } from "@/services/LoggingService";
import { useIsMobile } from "@/hooks/use-mobile";

// UI Components
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plug, CircleDollarSign } from "lucide-react";
import { Plus, Blocks, ChevronDown } from "lucide-react";

// Provider Components & Hooks
import { Wealthica2GoldContainer } from "@/providers/wealthica2/components/Wealthica2GoldContainer";
import { useWealthica2Status } from '@/providers/wealthica2/hooks/useWealthica2Status';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
// Local display shape for connection metadata (decoupled from DB hook)
interface ConnectionDisplayMeta {
  id?: string;
  institutionId: string;
  institutionName?: string;
  providerName: string;
  connectionStatus?: string;
}

import { useInvestmentHoldings } from '@/providers/manual/hooks/useInvestmentHoldings';
import { createBuildPortfolioHandlers } from "@/utils/buildPortfolioHandlers";

// Portfolio Components
import { InvestmentsSummaryHeader } from "@/components/investments/InvestmentsSummaryHeader";
import { EmptyStateView } from "@/components/investments/EmptyStateView";
import { AssetTypeFilterDialog } from "@/components/investments/AssetTypeFilterDialogNew";
import { useAddPositions } from "@/hooks/investments/useAddPositions";

// UI Navigation Components
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router-dom";
import { ComboButton } from "@/components/ui/ComboButton";
import { PageContainer } from "@/components/ui/PageContainer";
import { useProfile } from "@/hooks/useProfile";

function InvestmentsContent() {
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { profile } = useProfile(user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // State for dialogs and positions (Step 1 re-enabled)
  // const [, setShowManualAssetDialog] = useState(false);
  const [showAssetTypeFilterDialog, setShowAssetTypeFilterDialog] = useState(false);
  // const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [collectedPositions, setCollectedPositions] = useState<any[]>([]);
  const { openAddPositions } = useAddPositions();
  // const [pendingAssetTypeDialog, setPendingAssetTypeDialog] = useState(false);
  // const [showConnectionStatusDialog, setShowConnectionStatusDialog] = useState(false);

  // Wealthica state (re-enabled real hook)
  const wealthicaStatus = useWealthica2Status();
  const {
    isWealthicaConnected,
    isWealthicaLoading,
    hasConnectionData: hasWealthicaConnectionData
  } = wealthicaStatus;
  const SHOW_WEALTHICA_CONTAINER = true;

  // Use primitive selectors to avoid reference churn
  const connectionsLength = useMultiWealthica2Store((state) => state.connections.size);
  const hasConnections = connectionsLength > 0;
  const connectionsLoading = useMultiWealthica2Store((state) => state.isInitializing || state.isFetchingAllConnections);
  
  // Enhanced connection manager with multi-connection support
  // Removed duplicate hook instance to avoid unnecessary state updates
  
  // Multi-store primitive selectors to avoid unstable snapshots
  const multiStoreAccountsCount = useMultiWealthica2Store((state) => {
    let count = 0;
    state.connections.forEach((conn) => { count += (conn.accounts?.length || 0); });
    return count;
  });
  const isAnyStoreConnected = useMultiWealthica2Store((state) => {
    for (const conn of state.connections.values()) {
      if (conn.isConnected) return true;
    }
    return false;
  });
  // For UI-only metrics
  const multiStoreAccounts: any[] = [];
  
  // Handle updating specific connections with proper multi-connection logic
  const handleUpdateAccounts = useCallback((connection: ConnectionDisplayMeta) => {
    logger.info('Investments', 'User requested account update for connection', {
      institutionId: connection.institutionId,
      institutionName: connection.institutionName,
      providerName: connection.providerName,
      connectionStatus: connection.connectionStatus
    });
    
    // For multi-connection support, we need to switch to this connection
    // and then trigger account refresh
    try {
      // Create connection data for switching
      const connectionData = {
        institutionId: connection.institutionId,
        providerName: connection.providerName,
        timestamp: Date.now(),
        state: 'existing'
      };
      
      // We'll need to get the bearer token for this connection
      // Navigate to connection update flow
      navigate(`/investments/connect?institution=${connection.institutionId}&provider=${connection.providerName}&action=update`);
      
    } catch (error) {
      logger.error('Investments', 'Error updating connection accounts', {
        error: error instanceof Error ? error.message : String(error),
        connectionId: connection.id
      });
      toast.error('Failed to update connection. Please try again.');
    }
  }, [navigate]);

  // const {
  //   accounts: vezgoAccounts,
  //   isLoading: isVezgoLoading
  // } = useVezgoState();
  const vezgoAccounts: any[] = [];
  const isVezgoLoading = false;

  // Assets removed from Investments page (still used within hooks like useTotalAllocations)

  const {
    holdings: investmentHoldings,
    isLoading: isHoldingsLoading,
    refreshHoldings
  } = useInvestmentHoldings();

  // ENHANCED: React Query debugging for investment-related queries - DISABLED FOR PRODUCTION
  // const { debugQuery, invalidateQuery } = useReactQueryDebugger({
  //   queryKey: ['investmentHoldings', 'wealthica2', 'manualAssets'],
  //   logInterval: 10000, // Log every 10 seconds
  //   enableCacheMonitoring: true,
  //   enableStateTransitions: true
  // });

  // Header data derived from multi-store
  const getAllAccounts = useMultiWealthica2Store((state) => state.getAllAccounts);
  const getAllPositions = useMultiWealthica2Store((state) => state.getAllPositions);
  const headerAccounts = getAllAccounts ? getAllAccounts() : [];
  const headerPositionsCount = headerAccounts.length > 0 && getAllPositions
    ? (getAllPositions() as unknown[]).length
    : 0;

  // Account detection using proper data sources
  const dbHasConnections = connectionsLength > 0;
  const wealthicaHasAccounts = multiStoreAccountsCount > 0 || isAnyStoreConnected;
  const vezgoHasAccounts = (vezgoAccounts?.length || 0) > 0;
  const hasExternalHoldings = (investmentHoldings || []).filter(h => !h.is_manual).length > 0;
  // NEW: Explicit flag when there are no provider connections of any kind
  const noProviderConnections = !dbHasConnections && !wealthicaHasAccounts && !vezgoHasAccounts;

  // Debug-only rerender instrumentation removed to reduce render churn

  // Connect to provider selection page
  const connectProvider = useCallback(() => {
    if (!user) {
      toast.error('You must be logged in to connect investment accounts.');
      return;
    }
    navigate('/investments/connect');
    logger.info('Investments', 'Navigating to provider selection page');
  }, [user, navigate]);

  // Simple connect button - always navigates to connect page
  const handleConnect = useCallback(() => {
    if (!user) {
      toast.error('You must be logged in to connect investment accounts.');
      return;
    }
    navigate('/investments/connect');
    logger.info('Investments', 'Navigating to provider selection page');
  }, [user, navigate]);

  // Check for any provider-related data loading (exclude allocations to avoid blocking empty state)
  const isAnyLoading = isWealthicaLoading || isVezgoLoading || connectionsLoading || isHoldingsLoading;
  
  // Note: Race condition fix now handled by passing positions directly in buildPortfolioHandlers

  // Unified account detection logic - single source of truth
  const hasAnyAccountsOrData = dbHasConnections || wealthicaHasAccounts || vezgoHasAccounts || hasExternalHoldings;
  
  // View-only assets detector: enable Build Portfolio when positions exist in memory
  // even if persistence is OFF 
  const hasViewOnlyAssets = useMultiWealthica2Store((state) => {
    for (const conn of state.connections.values()) {
      if ((conn.positions?.length || 0) > 0) return true;
    }
    return false;
  });
  
  // Determine what to show - prioritize Empty State when there are no provider connections
  const showEmptyState = noProviderConnections && !isAnyLoading;
  const showInvestmentAccounts = !noProviderConnections && (hasAnyAccountsOrData || (isAnyLoading && dbHasConnections));
  
  // Use investment holdings for accurate asset counting - FIXED: bulletproof null safety
  const hasInvestmentAssets = (investmentHoldings || []).length > 0;

  // Create build portfolio handlers (Step 3 re-enabled)
  const { handleBuildPortfolio, handleBuildPortfolioManualOnly } = createBuildPortfolioHandlers({
    investmentHoldings,
    refreshHoldings,
    setCollectedPositions,
    setShowAssetTypeFilterDialog
  });

  // Log account detection once per state change to avoid re-entrant noise
  useEffect(() => {
    logger.debug('Investments', 'Account detection state', {
      dbHasConnections,
      wealthicaHasAccounts,
      vezgoHasAccounts,
      hasExternalHoldings,
      multiStoreAccountsCount: multiStoreAccounts.length,
      isAnyStoreConnected,
      hasAnyAccountsOrData,
      noProviderConnections,
      showEmptyState,
      showInvestmentAccounts,
      isAnyLoading,
      connectionsCount: connectionsLength,
      investmentHoldingsCount: investmentHoldings?.length || 0
    });
  }, [
    dbHasConnections,
    wealthicaHasAccounts,
    vezgoHasAccounts,
    hasExternalHoldings,
    multiStoreAccounts.length,
    isAnyStoreConnected,
    hasAnyAccountsOrData,
    noProviderConnections,
    showEmptyState,
    showInvestmentAccounts,
    isAnyLoading,
    connectionsLength,
    investmentHoldings?.length
  ]);

  return (
    <div className="flex-1">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 sticky top-0 bg-background z-10">
        <SidebarTrigger className="-ml-2" />
        <Separator orientation="vertical" className="h-6" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Investments</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </header>
      <PageContainer maxWidthClassName="max-w-[960px]">
        <div className="flex flex-col">
          {/* Title and subtitle area */}
          <div className="flex flex-col">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Investment Accounts</h1>
                <p className="text-sm text-muted-foreground mt-1">View all your accounts and assets in one place.</p>
              </div>
              
              {/* Portfolio actions - responsive stacking */}
              <div className="flex w-full sm:w-auto">
                <ComboButton
                  label="Build Portfolio"
                  icon={<Blocks className="w-4 h-4" />}
                  onPrimaryClick={handleBuildPortfolio}
                  size="sm"
                  variant="outline"
                  className="self-start min-h-[44px] whitespace-nowrap"
                  disabled={showEmptyState || !(hasInvestmentAssets || hasViewOnlyAssets)}
                  menuItems={[
                    {
                      label: "Import from CSV",
                      onClick: () => {},
                      icon: <Plus className="w-4 h-4" />,
                      disabled: true,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        
          {/* Investments summary header in page header (blue box) */}
          {showInvestmentAccounts && !isAnyLoading && (
            <div className="mt-6 mb-6">
              <InvestmentsSummaryHeader 
                accounts={headerAccounts as any}
                positionsCount={headerPositionsCount}
                defaultCurrency={(profile?.default_currency || 'CAD') as any}
              />
            </div>
          )}
        
          {/* Empty state when no accounts connected */}
          {showEmptyState && (
            <div className="mt-6">
              <EmptyStateView 
                onConnectWealthica={connectProvider}
                onAddManualAsset={() => navigate('/assets')}
              />
            </div>
          )}
        
          {/* INVESTMENT ACCOUNTS Section - Show only when there are connections/accounts */}
          {showInvestmentAccounts && (
            <div className="w-full flex flex-col gap-4">
              <Card className="p-4 shadow-md border mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <CircleDollarSign className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-medium text-slate-600 uppercase tracking-wide">INVESTMENT ACCOUNTS</h3>
                  </div>
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="gap-1 bg-slate-900 hover:bg-slate-800 text-white min-h-[44px] w-full sm:w-auto"
                    onClick={handleConnect}
                    disabled={connectionsLoading}
                  >
                    <Plug className="w-4 h-4" />
                    {isMobile ? "Connect Account" : "Connect"}
                  </Button>
                </div>

                {/* Conditionally show Wealthica provider content */}
                {SHOW_WEALTHICA_CONTAINER && isWealthicaConnected && (
                  <Wealthica2GoldContainer />
                )}
                
              </Card>
            </div>
          )}
        
        </div>
      </PageContainer>
        
        {/* Dialogs (Step 4 re-enabled) */}
        <AssetTypeFilterDialog
          open={showAssetTypeFilterDialog}
          onOpenChange={setShowAssetTypeFilterDialog}
          positions={collectedPositions}
          onContinue={(positions) => {
            setShowAssetTypeFilterDialog(false);
            openAddPositions({
              positions,
              navigateOnZeroAdded: true,
              onClose: () => {
                setCollectedPositions([]);
              },
            });
          }}
        />
    </div>
  );
}

// Export the component without provider wrapper
export default function Investments() {
  return (
    <>
      <InvestmentsContent />
      {/* <PerformanceDebugger /> */}
    </>
  );
}
